import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

interface User {
  id: string;
  email: string;
  role: string;
  account_status: string;
  is_active: boolean;
  is_internal_account?: boolean;
  created_at: string;
  last_login: string | null;
  total_compute_hours: number;
  total_spend: number;
  account_balance: number;
  current_month_cost: number;
  current_month_hours: number;
  has_stripe_customer: boolean;
  subscription_status: string;
}

interface UserUpdate {
  role?: string;
  account_status?: string;
  is_active?: boolean;
  is_internal_account?: boolean;
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<UserUpdate>({});
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchUsers();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async (search?: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const response = await axios.get(`${getApiUrl()}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers(searchTerm);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      role: user.role,
      account_status: user.account_status,
      is_active: user.is_active,
      is_internal_account: user.is_internal_account ?? false,
    });
    setError(null); // Clear any existing errors
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setEditForm({});
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setUpdating(true);
      setError(null); // Clear any existing errors
      const token = localStorage.getItem('authToken');
      
      const response = await axios.put(
        `${getApiUrl()}/admin/users/${selectedUser.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('User update response:', response.data);
      
      // Refresh users list
      await fetchUsers(searchTerm);
      closeEditModal();
    } catch (err: any) {
      console.error('Error updating user:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update user';
      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400';
      case 'enterprise':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'suspended':
        return 'bg-red-500/20 text-red-400';
      case 'canceled':
        return 'bg-orange-500/20 text-orange-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
            <p className="text-gray-400">
              Manage user accounts, roles, and permissions
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={() => fetchUsers(searchTerm)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-8">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users by email..."
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  fetchUsers();
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
            <div className="text-red-400">{error}</div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-6 border-b border-gray-800">
            <h2 className="text-xl font-bold text-white">All Users ({users.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">User</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Role</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Status</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Usage (Month)</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Total Spend</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Balance</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Last Login</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Stripe</th>
                  <th className="px-6 py-4 text-sm font-medium text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-800/30">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">{user.email}</div>
                        <div className="text-xs text-gray-400">
                          ID: {user.id} • Created: {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full w-fit ${getStatusColor(user.account_status)}`}>
                          {user.account_status}
                        </span>
                        {!user.is_active && (
                          <span className="text-xs text-red-400">Inactive</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        <div>${user.current_month_cost.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">{user.current_month_hours.toFixed(1)} hours</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        <div>${user.total_spend.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">{user.total_compute_hours.toFixed(1)}h total</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        <div className={`font-medium ${user.account_balance >= 5 ? 'text-green-400' : user.account_balance >= 2 ? 'text-yellow-400' : 'text-red-400'}`}>
                          ${user.account_balance.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.account_balance >= 5 ? 'Can launch' : user.account_balance >= 2 ? 'Low balance' : 'Top-up needed'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleString()
                        : 'Never'
                      }
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${user.has_stripe_customer ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-xs text-gray-400">
                          {user.has_stripe_customer ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                      {user.subscription_status !== 'inactive' && (
                        <div className="text-xs text-gray-500 mt-1">
                          Sub: {user.subscription_status}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400">
                {searchTerm ? 'No users found matching your search' : 'No users found'}
              </div>
            </div>
          )}
        </div>

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Edit User</h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="text"
                    value={selectedUser.email}
                    readOnly
                    className="w-full bg-gray-800 border border-gray-700 text-gray-400 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={editForm.role || ''}
                    onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Account Status
                  </label>
                  <select
                    value={editForm.account_status || ''}
                    onChange={(e) => setEditForm({...editForm, account_status: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editForm.is_active || false}
                    onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                    className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-700 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-300">
                    Account is active
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_internal_account"
                    checked={editForm.is_internal_account || false}
                    onChange={(e) => setEditForm({...editForm, is_internal_account: e.target.checked})}
                    className="w-4 h-4 text-yellow-600 bg-gray-800 border-gray-700 rounded focus:ring-yellow-500"
                  />
                  <label htmlFor="is_internal_account" className="ml-2 text-sm text-gray-300">
                    Internal account <span className="text-gray-500">(opex billing)</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleUpdateUser}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg font-medium transition-colors"
                >
                  {updating ? 'Updating...' : 'Update User'}
                </button>
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}